import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../hooks/useApi';
import DateInput from '../components/DateInput';
import { SectionProduitFini } from '../components/TracabiliteSections';
import EtapeTracabilite from '../components/EtapeTracabilite';
import { creerFormulaireTraiteFinal } from '../utils/tracabilite';
import BatchAnalytiquePanel from '../components/BatchAnalytiquePanel';
import CoutRevient from '../components/CoutRevient';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import RecetteStockPanel from '../components/RecetteStockPanel';
import Icon from '../components/Icon';
import './ProductionSauce.css';

// ─── CONSTANTES ───────────────────────────────

const ETAPES = [
  { id: 1, label: 'Batch' },
  { id: 2, label: 'Traçabilité' },
  { id: 3, label: 'Recette' },
  { id: 4, label: 'Production & Push' },
];

// ─── HELPERS ──────────────────────────────────

function fmtMl(ml) {
  if (ml === null || ml === undefined) return '—';
  return ml >= 1000 ? `${(ml / 1000).toFixed(2)} L` : `${ml} ml`;
}

function yieldBadge(pct) {
  if (!pct) return null;
  if (pct >= 90)  return { cls: 'badge-success', label: `${pct.toFixed(1)}% OK` };
  if (pct >= 75)  return { cls: 'badge-warning', label: `${pct.toFixed(1)}%` };
  return { cls: 'badge-danger', label: `${pct.toFixed(1)}% faible` };
}

function copierListeAchats(recette, multiplicateur) {
  if (!recette) return;
  const lines = [
    `=== LISTE D'ACHATS — Sauce BBQ × ${multiplicateur} ===`,
    '',
    '--- KETCHUP MAISON ---',
    `Pâte de tomate bio       : ${recette.ketchup.pate_tomate_cans} cans (156ml)`,
    `Vinaigre de cidre pomme  : ${fmtMl(recette.ketchup.vinaigre_cidre_ml)}`,
    `Vinaigre blanc           : ${fmtMl(recette.ketchup.vinaigre_blanc_ml)}`,
    `Miel                     : ${fmtMl(recette.ketchup.miel_ml)}`,
    `Eau                      : ${fmtMl(recette.ketchup.eau_ml)}`,
    `Tallow fumé              : ${fmtMl(recette.ketchup.tallow_ml)}`,
    `Onion granulé            : ${fmtMl(recette.ketchup.onion_ml)}`,
    `Ail granulé              : ${fmtMl(recette.ketchup.ail_ml)}`,
    `Sel Kasher               : ${fmtMl(recette.ketchup.sel_kasher_ml)}`,
    `Sel de céleri            : ${fmtMl(recette.ketchup.sel_celeri_ml)}`,
    `Paprika fumé             : ${fmtMl(recette.ketchup.paprika_ml)}`,
    `Sauce soya               : ${fmtMl(recette.ketchup.sauce_soya_ml)}`,
    `Cassonade                : ${fmtMl(recette.ketchup.cassonade_ml)}`,
    `→ Rendement ketchup estimé : ~${recette.ketchup.rendement_estime_ml}ml`,
    '',
    '--- SAUCE BBQ MIEL & BOURBON ---',
    'GASTRIQUE :',
    `Vinaigre de cidre pomme  : ${fmtMl(recette.sauce.vinaigre_cidre_ml)}`,
    `Sucre de canne           : ${fmtMl(recette.sauce.sucre_canne_ml)}`,
    `Cassonade                : ${fmtMl(recette.sauce.cassonade_ml)}`,
    `Worcestershire           : ${fmtMl(recette.sauce.worcestershire_ml)}`,
    `Sauce soya               : ${fmtMl(recette.sauce.sauce_soya_ml)}`,
    'ÉPICES :',
    `Sel Kasher               : ${fmtMl(recette.sauce.sel_kasher_ml)}`,
    `Poivre moulu             : ${fmtMl(recette.sauce.poivre_ml)}`,
    `Poivre blanc             : ${fmtMl(recette.sauce.poivre_blanc_ml)}`,
    `Poudre de Chili          : ${fmtMl(recette.sauce.chili_ml)}`,
    `Ail granulé              : ${fmtMl(recette.sauce.ail_ml)}`,
    `Onion granulé            : ${fmtMl(recette.sauce.onion_ml)}`,
    `Épices de Jamaïque       : ${fmtMl(recette.sauce.epices_jamaique_ml)}`,
    `Cumin                    : ${fmtMl(recette.sauce.cumin_ml)}`,
    'CORPS :',
    `Ketchup maison           : ${fmtMl(recette.sauce.ketchup_ml)} (du ketchup ci-dessus)`,
    `Miel                     : ${fmtMl(recette.sauce.miel_ml)}`,
    `Eau                      : ${fmtMl(recette.sauce.eau_ml)}`,
    `Bourbon américain        : ${fmtMl(recette.sauce.bourbon_ml)}`,
    `→ Rendement sauce estimé : ~${recette.sauce.rendement_estime_ml}ml`,
  ];
  navigator.clipboard?.writeText(lines.join('\n'));
}

// ─── STEPPER ──────────────────────────────────

function Stepper({ etape, nbTracabiliteNonLies = 0, onStepClick, hasBatch, isFinalise }) {
  const peutCliquer = (cible) => {
    if (!hasBatch) return cible === 1;
    // Dès qu'on a un batch (reprise ou nouveau), toutes les étapes sont accessibles
    return true;
  };

  return (
    <div className="stepper">
      {ETAPES.map((e, i) => {
        const clickable = onStepClick && peutCliquer(e.id);
        return (
          <React.Fragment key={e.id}>
            <div
              className={`step ${etape === e.id ? 'active' : etape > e.id ? 'done' : ''} ${clickable ? 'step-cliquable' : ''}`}
              style={{ position: 'relative', cursor: clickable ? 'pointer' : 'default' }}
              onClick={() => clickable && onStepClick(e.id)}
              title={clickable ? `Aller à l'étape ${e.id} — ${e.label}` : ''}
            >
              <div className="step-circle">
                {etape > e.id ? <Icon name="check" size={14} weight="bold" /> : e.id}
              </div>
              {e.id === 2 && nbTracabiliteNonLies > 0 && (
                <span className="step-badge-warning">{nbTracabiliteNonLies} !</span>
              )}
              <span className="step-label">{e.label}</span>
            </div>
            {i < ETAPES.length - 1 && (
              <div className={`step-line ${etape > e.id ? 'done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── ÉTAPE 1 — Batch ──────────────────────────

function EtapeBatch({ onNext, recette }) {
  const [nomAuto, setNomAuto] = useState('');
  const [nomManuel, setNomManuel] = useState(false);

  const [form, setForm] = useState({
    nom          : '',
    date_prod    : new Date().toISOString().split('T')[0],
    operateurs   : '',
    multiplicateur: 1,
    prix_pot_250 : recette?.prix_pot_250ml || 10.00,
    prix_pot_350 : recette?.prix_pot_350ml || 14.00,
    notes        : '',
  });
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur]   = useState(null);
  const submittingRef = useRef(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('/batches/prochain-numero/sauce')
      .then(r => { setNomAuto(r.nom); setForm(p => ({ ...p, nom: p.nom || r.nom })); })
      .catch(() => {});
  }, []); // eslint-disable-line

  const rendementTheo = Math.round((recette?.rendement_sauce_ml || 1200) * form.multiplicateur);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.operateurs) { setErreur('Veuillez sélectionner les opérateurs.'); return; }
    // Anti-double-clic
    if (submittingRef.current) return;
    if (loading) return;
    submittingRef.current = true;
    setLoading(true);
    setErreur(null);
    try {
      const nomEffectif = form.nom.trim() || nomAuto || undefined;
      const res = await api.post('/sauce/batches', {
        nom           : nomEffectif,
        date_prod     : form.date_prod,
        operateurs    : form.operateurs,
        multiplicateur: form.multiplicateur,
        prix_pot_250  : form.prix_pot_250,
        prix_pot_350  : form.prix_pot_350,
        notes         : form.notes,
      });
      onNext({ batchId: res.id, form, recetteCalc: res.recette, rendementTheo: res.rendement_theorique_ml });
    } catch (err) {
      setErreur(err.response?.data?.error || err.message);
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  }

  return (
    <form className="sauce-form" onSubmit={handleSubmit}>
      <RecetteStockPanel produit="sauce" multiplicateur={form.multiplicateur} />

      <div className="form-grid">
        <div className="form-group">
          <label>Numéro du batch</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              className="form-input"
              value={form.nom}
              onChange={e => set('nom', e.target.value)}
              readOnly={!nomManuel}
              style={{ flex: 1, opacity: nomManuel ? 1 : 0.75, background: nomManuel ? undefined : 'rgba(255,255,255,0.04)' }}
              placeholder={nomAuto || 'Chargement…'}
            />
            <button type="button" className="btn btn-ghost btn-sm"
              onClick={() => setNomManuel(m => !m)} style={{ whiteSpace: 'nowrap' }}>
              {nomManuel ? '↩ Auto' : '✎ Modifier'}
            </button>
          </div>
          <span className="muted" style={{ fontSize: 12 }}>Numéro auto-généré</span>
        </div>
        <div className="form-group">
          <label>Date de production *</label>
          <DateInput
            className="form-input"
            value={form.date_prod}
            onChange={v => set('date_prod', v)}
          />
        </div>
        <div className="form-group">
          <label>Opérateurs *</label>
          <select
            className="form-input"
            value={form.operateurs}
            onChange={e => set('operateurs', e.target.value)}
            required
          >
            <option value="">— Sélectionner —</option>
            <option value="LCU">LCU</option>
            <option value="ELA">ELA</option>
            <option value="LCU+ELA">Les deux</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Multiplicateur de recette</label>
        <div className="multiplicateur-ctrl">
          <button type="button" className="mult-btn" onClick={() => set('multiplicateur', Math.max(1, form.multiplicateur - 1))}>−</button>
          <input
            className="form-input mult-input"
            type="number"
            min="1"
            max="20"
            value={form.multiplicateur}
            onChange={e => set('multiplicateur', parseInt(e.target.value) || 1)}
          />
          <button type="button" className="mult-btn" onClick={() => set('multiplicateur', form.multiplicateur + 1)}>+</button>
        </div>
        <div className="rendement-hint">
          Rendement estimé : <strong>~{fmtMl(rendementTheo)}</strong> de sauce
        </div>
      </div>

      <div className="form-grid prix-grid">
        <div className="form-group">
          <label>Prix de vente au détail — pot 250ml ($)</label>
          <input
            className="form-input"
            type="number"
            step="0.01"
            min="0"
            value={form.prix_pot_250}
            onChange={e => set('prix_pot_250', parseFloat(e.target.value) || 0)}
          />
        </div>
        <div className="form-group">
          <label>Prix de vente au détail — pot 350ml ($)</label>
          <input
            className="form-input"
            type="number"
            step="0.01"
            min="0"
            value={form.prix_pot_350}
            onChange={e => set('prix_pot_350', parseFloat(e.target.value) || 0)}
          />
        </div>
      </div>

      <div className="form-group">
        <label>Notes</label>
        <textarea
          className="form-input"
          rows="2"
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          placeholder="Notes de production..."
        />
      </div>

      {erreur && <div className="alert alert-danger">{erreur}</div>}


      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={loading || !form.operateurs}>
          {loading ? 'Création...' : 'Suivant — Voir la recette →'}
        </button>
      </div>
    </form>
  );
}

// ─── ÉTAPE 1 BIS — Édition d'un batch existant ─

function EtapeBatchEditer({ batchData, onUpdated }) {
  const batch = batchData.form;
  const isFinalise = batch.statut === 'finalise';
  const [form, setForm] = useState({
    date_prod     : batch.date_prod || '',
    operateurs    : batch.operateurs || '',
    multiplicateur: batch.multiplicateur || 1,
    prix_pot_250  : batch.prix_pot_250 || 10.00,
    prix_pot_350  : batch.prix_pot_350 || 14.00,
    notes         : batch.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur]   = useState('');
  const [toast, setToast]     = useState('');

  const enregistrer = async () => {
    setLoading(true);
    setErreur('');
    setToast('');
    try {
      const updated = await api.put(`/sauce/batches/${batchData.batchId}`, {
        date_prod     : form.date_prod || null,
        operateurs    : form.operateurs || null,
        multiplicateur: parseFloat(form.multiplicateur) || null,
        prix_pot_250  : parseFloat(form.prix_pot_250) || null,
        prix_pot_350  : parseFloat(form.prix_pot_350) || null,
        notes         : form.notes || null,
      });
      // Recharger la recette calculée si multiplicateur a changé
      let recetteCalc = batchData.recetteCalc;
      let rendementTheo = batchData.rendementTheo;
      if (parseFloat(form.multiplicateur) !== parseFloat(batch.multiplicateur)) {
        try {
          const calc = await api.get(`/sauce/calculateur?multiplicateur=${form.multiplicateur}`);
          recetteCalc = calc;
          rendementTheo = updated.rendement_theorique_ml;
        } catch { /* dégradé */ }
      }
      onUpdated({
        batchId: batchData.batchId,
        form: updated,
        recetteCalc,
        rendementTheo,
      });
      setToast('Modifications enregistrées');
      setTimeout(() => setToast(''), 3000);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card form-card">
      <h3>
        Modifier les informations — <span className="gold">{batch.nom}</span>
        {isFinalise && (
          <span className="badge badge-success" style={{ marginLeft: 8, fontSize: 11 }}>Finalisé</span>
        )}
      </h3>
      {erreur && <p className="danger" style={{ marginBottom: 12 }}>{erreur}</p>}
      {toast && <p className="success" style={{ marginBottom: 12 }}>{toast}</p>}

      <div className="form-grid">
        <div className="form-group">
          <label>Date de production</label>
          <DateInput value={form.date_prod} onChange={v => setForm({ ...form, date_prod: v })} />
        </div>
        <div className="form-group">
          <label>Opérateurs</label>
          <select
            className="form-input"
            value={form.operateurs}
            onChange={e => setForm({ ...form, operateurs: e.target.value })}
          >
            <option value="">— Sélectionner —</option>
            <option value="LCU">LCU</option>
            <option value="ELA">ELA</option>
            <option value="LCU+ELA">Les deux</option>
          </select>
        </div>
        <div className="form-group">
          <label>Multiplicateur</label>
          <input
            type="number" min="1" max="20"
            className="form-input"
            value={form.multiplicateur}
            disabled={isFinalise}
            onChange={e => setForm({ ...form, multiplicateur: parseInt(e.target.value) || 1 })}
          />
        </div>
        <div className="form-group">
          <label>Prix pot 250ml ($)</label>
          <input
            type="number" step="0.01" min="0"
            className="form-input"
            value={form.prix_pot_250}
            disabled={isFinalise}
            onChange={e => setForm({ ...form, prix_pot_250: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Prix pot 350ml ($)</label>
          <input
            type="number" step="0.01" min="0"
            className="form-input"
            value={form.prix_pot_350}
            disabled={isFinalise}
            onChange={e => setForm({ ...form, prix_pot_350: e.target.value })}
          />
        </div>
      </div>

      <div className="form-group">
        <label>Notes</label>
        <textarea
          className="form-input"
          rows="2"
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
        />
      </div>

      <div className="form-actions">
        <button className="btn btn-primary" onClick={enregistrer} disabled={loading}>
          {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </button>
      </div>
      {isFinalise && (
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Batch finalisé : multiplicateur et prix verrouillés. Date, opérateurs et notes restent éditables.
        </p>
      )}
    </div>
  );
}

// ─── TABLEAU INGRÉDIENTS ──────────────────────

function TableIngredients({ rows }) {
  return (
    <table className="esc-table ingredients-table">
      <thead>
        <tr>
          <th>Ingrédient</th>
          <th>Quantité</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, valeur]) => (
          <tr key={label}>
            <td>{label}</td>
            <td className="qte-cell">{valeur}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── ÉTAPE 2 — Recette calculée ───────────────

function EtapeRecette({ batchData, recetteCalc, onNext, onBack }) {
  if (!recetteCalc) return <div className="muted">Chargement recette…</div>;

  const k = recetteCalc.ketchup;
  const s = recetteCalc.sauce;

  const rowsKetchup = [
    ['Pâte de tomate bio (Kirkland)', `${k.pate_tomate_cans} cans (156ml)`],
    ['Tallow fumé', fmtMl(k.tallow_ml)],
    ['Vinaigre de cidre de pomme', fmtMl(k.vinaigre_cidre_ml)],
    ['Vinaigre blanc', fmtMl(k.vinaigre_blanc_ml)],
    ['Miel', fmtMl(k.miel_ml)],
    ['Eau', fmtMl(k.eau_ml)],
    ['Onion granulé', fmtMl(k.onion_ml)],
    ['Ail granulé', fmtMl(k.ail_ml)],
    ['Sel Kasher', fmtMl(k.sel_kasher_ml)],
    ['Sel de céleri', fmtMl(k.sel_celeri_ml)],
    ['Paprika fumé', fmtMl(k.paprika_ml)],
    ['Sauce soya', fmtMl(k.sauce_soya_ml)],
    ['Cassonade', fmtMl(k.cassonade_ml)],
  ];

  const rowsGastrique = [
    ['Vinaigre de cidre de pomme', fmtMl(s.vinaigre_cidre_ml)],
    ['Sucre de canne biologique', fmtMl(s.sucre_canne_ml)],
    ['Cassonade', fmtMl(s.cassonade_ml)],
    ['Sauce Worcestershire', fmtMl(s.worcestershire_ml)],
    ['Sauce soya', fmtMl(s.sauce_soya_ml)],
  ];

  const rowsEpices = [
    ['Sel Kasher', fmtMl(s.sel_kasher_ml)],
    ['Poivre moulu', fmtMl(s.poivre_ml)],
    ['Poivre blanc', fmtMl(s.poivre_blanc_ml)],
    ['Poudre de Chili', fmtMl(s.chili_ml)],
    ['Ail granulé', fmtMl(s.ail_ml)],
    ['Onion granulé', fmtMl(s.onion_ml)],
    ['Épices de Jamaïque', fmtMl(s.epices_jamaique_ml)],
    ['Cumin', fmtMl(s.cumin_ml)],
  ];

  const rowsCorps = [
    ['Ketchup maison', `${fmtMl(s.ketchup_ml)} (du ketchup ci-dessus)`],
    ['Miel', fmtMl(s.miel_ml)],
    ['Eau', fmtMl(s.eau_ml)],
    ['Bourbon américain', `${fmtMl(s.bourbon_ml)}`],
  ];

  return (
    <div className="etape-recette">
      {/* KETCHUP */}
      <div className="section-recette ketchup-section">
        <div className="section-header">
          <span className="section-icon"><Icon name="epices" size={20} /></span>
          <div>
            <h3>KETCHUP MAISON</h3>
            <span className="section-sub">À préparer en premier — base de la sauce</span>
          </div>
        </div>
        <div className="prep-steps">
          <div className="prep-step">
            <span className="prep-num">1</span>
            Chauffer le Tallow fumé, cuire la pâte de tomate à feu doux/moyen 10 min
          </div>
          <div className="prep-step">
            <span className="prep-num">2</span>
            Incorporer tous les ingrédients, chauffer jusqu'à ébullition
          </div>
          <div className="prep-step">
            <span className="prep-num">3</span>
            Retirer du feu
          </div>
        </div>
        <TableIngredients rows={rowsKetchup} />
        <div className="rendement-row">
          Rendement ketchup estimé : <strong>~{fmtMl(k.rendement_estime_ml)}</strong>
        </div>
      </div>

      {/* SAUCE BBQ */}
      <div className="section-recette sauce-section">
        <div className="section-header">
          <span className="section-icon"><Icon name="fumage" size={20} /></span>
          <div>
            <h3>SAUCE BBQ MIEL &amp; BOURBON</h3>
            <span className="section-sub">Étape 2 — utilise le ketchup maison</span>
          </div>
        </div>
        <div className="prep-steps">
          <div className="prep-step">
            <span className="prep-num">1</span>
            Faire chauffer le <strong>Gastrique</strong> à ébullition, réduire à feu doux 10 min
          </div>
          <div className="prep-step">
            <span className="prep-num">2</span>
            Incorporer les <strong>Épices</strong>, laisser reposer 30 minutes
          </div>
          <div className="prep-step">
            <span className="prep-num">3</span>
            Ajouter le <strong>Corps</strong>, chauffer à ébullition, remuer, reposer 30 minutes
          </div>
        </div>

        <div className="sauce-sous-section">
          <div className="sous-section-titre">GASTRIQUE</div>
          <TableIngredients rows={rowsGastrique} />
        </div>
        <div className="sauce-sous-section">
          <div className="sous-section-titre">ÉPICES</div>
          <TableIngredients rows={rowsEpices} />
        </div>
        <div className="sauce-sous-section">
          <div className="sous-section-titre">CORPS</div>
          <TableIngredients rows={rowsCorps} />
        </div>
        <div className="rendement-row">
          Rendement sauce estimé : <strong>~{fmtMl(s.rendement_estime_ml)}</strong>
        </div>
      </div>

      <div className="recette-actions">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => copierListeAchats(recetteCalc, batchData.form.multiplicateur)}
        >
          📋 Copier la liste d'achats
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => window.print()}
        >
          🖨 Imprimer la liste
        </button>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-ghost" onClick={onBack}>← Retour</button>
        <button type="button" className="btn btn-primary" onClick={onNext}>
          Suivant — Enregistrer la production →
        </button>
      </div>
    </div>
  );
}

// ─── ÉTAPE 3 — Production & Push ─────────────

function EtapeProduction({ batchData, onSuccess, onBack }) {
  // Init en cascade : (1) valeurs finalisées, (2) brouillon JSON, (3) vide
  const [pots250, setPots250] = useState(() => {
    if (batchData?.form?.nb_pots_250 > 0) return String(batchData.form.nb_pots_250);
    if (batchData?.form?.pots_brouillon_json) {
      try {
        const parsed = JSON.parse(batchData.form.pots_brouillon_json);
        if (parsed.nb_pots_250 > 0) return String(parsed.nb_pots_250);
      } catch { /* fallback */ }
    }
    return '';
  });
  const [pots350, setPots350] = useState(() => {
    if (batchData?.form?.nb_pots_350 > 0) return String(batchData.form.nb_pots_350);
    if (batchData?.form?.pots_brouillon_json) {
      try {
        const parsed = JSON.parse(batchData.form.pots_brouillon_json);
        if (parsed.nb_pots_350 > 0) return String(parsed.nb_pots_350);
      } catch { /* fallback */ }
    }
    return '';
  });
  const [wcStock, setWcStock] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur]   = useState(null);
  const [resultat, setResultat] = useState(null);
  const [apercuDeduction, setApercuDeduction] = useState(null);
  const [finalForm, setFinalForm] = useState(() => creerFormulaireTraiteFinal('sauce'));
  const submittingRef = useRef(false);

  const rendTheo = parseFloat(batchData.rendementTheo) || 0;
  const isFinalise = batchData?.form?.statut === 'finalise';

  useEffect(() => {
    api.get('/sauce/stock/wc').then(r => setWcStock(r)).catch(() => {});
    if (batchData.batchId) {
      // Inclure les pots saisis pour avoir un aperçu pertinent
      const params = new URLSearchParams();
      if (parseInt(pots250) > 0) params.append('nb_pots_250', pots250);
      if (parseInt(pots350) > 0) params.append('nb_pots_350', pots350);
      const qs = params.toString() ? `?${params.toString()}` : '';
      api.get(`/sauce/batches/${batchData.batchId}/apercu-deduction${qs}`)
        .then(setApercuDeduction)
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  // Auto-save brouillon (sur blur)
  const sauverBrouillon = async () => {
    if (isFinalise) return;
    if (!batchData?.batchId) return;
    try {
      await api.put(`/sauce/batches/${batchData.batchId}/pots-brouillon`, {
        nb_pots_250: parseInt(pots250) || 0,
        nb_pots_350: parseInt(pots350) || 0,
      });
    } catch { /* silencieux — brouillon */ }
  };

  const mlProduits = (parseInt(pots250) || 0) * 250 + (parseInt(pots350) || 0) * 350;
  const yieldPct   = rendTheo > 0 ? (mlProduits / rendTheo * 100) : 0;
  const ecart      = mlProduits - rendTheo;
  const badge      = yieldBadge(yieldPct > 0 ? yieldPct : null);

  const prix250 = parseFloat(batchData.form.prix_pot_250) || 10;
  const prix350 = parseFloat(batchData.form.prix_pot_350) || 14;
  const revenus = Math.round(((parseInt(pots250)||0)*prix250 + (parseInt(pots350)||0)*prix350)*100)/100;

  async function handleSubmit(e) {
    e.preventDefault();
    if (submittingRef.current) return;
    if (loading) return;
    submittingRef.current = true;
    setLoading(true);
    setErreur(null);
    try {
      const res = await api.post(`/sauce/batches/${batchData.batchId}/production`, {
        nb_pots_250: parseInt(pots250) || 0,
        nb_pots_350: parseInt(pots350) || 0,
        ...finalForm,
      });
      setResultat(res);
      onSuccess(res);
    } catch (err) {
      setErreur(err.response?.data?.error || err.message);
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  }

  if (resultat) {
    const b = yieldBadge(resultat.yield_sauce);
    return (
      <div className="resultat-ok">
        <div className="resultat-icon"><Icon name="succes" size={24} /></div>
        <h3>Batch finalisé avec succès !</h3>
        <div className="resultat-grid">
          <div className="stat-box">
            <div className="stat-val">{resultat.nb_pots_250}</div>
            <div className="stat-lbl">Pots 250ml</div>
          </div>
          <div className="stat-box">
            <div className="stat-val">{resultat.nb_pots_350}</div>
            <div className="stat-lbl">Pots 350ml</div>
          </div>
          <div className="stat-box">
            <div className="stat-val">{fmtMl(resultat.rendement_reel_ml)}</div>
            <div className="stat-lbl">Rendement réel</div>
          </div>
          <div className="stat-box">
            <div className={`stat-val badge ${b?.cls}`}>{b?.label || '—'}</div>
            <div className="stat-lbl">Yield</div>
          </div>
        </div>
        {resultat.wc_sync?.success && (
          <div className="alert alert-success">
            WooCommerce mis à jour — SBBQMW-250 et SBBQMW-350 synchronisés
          </div>
        )}
        {resultat.wc_sync?.errors?.length > 0 && (
          <div className="alert alert-warning">
            Erreur WC : {resultat.wc_sync.errors.join(', ')}
          </div>
        )}
        <div className="revenus-line">
          Revenus potentiels : <strong className="gold">{resultat.revenus_potentiels?.toFixed(2)} $</strong>
        </div>
        {resultat.deductions?.length > 0 && (
          <div style={{ marginTop: 16, textAlign: 'left' }}>
            <h4 style={{ marginBottom: 10 }}>Inventaire déduit</h4>
            <table className="esc-table">
              <thead>
                <tr><th>Ingrédient</th><th>Déduit</th><th>Stock restant</th></tr>
              </thead>
              <tbody>
                {resultat.deductions.map(d => (
                  <tr key={d.matiere_id}>
                    <td>{d.nom}</td>
                    <td className="gold">−{d.quantite_deduite} {d.unite}</td>
                    <td className={d.statut === 'negatif' ? 'danger' : d.statut === 'bas' ? 'warning' : 'success'}>
                      {d.stock_apres} {d.unite}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {resultat.deductions.some(d => d.statut !== 'ok') && (
              <p className="warning" style={{ marginTop: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="attention" size={14} /> {resultat.deductions.filter(d => d.statut !== 'ok').length} ingrédient(s) à réapprovisionner
              </p>
            )}
          </div>
        )}
        <CoutRevient analytique={resultat.analytique} revenus={resultat.revenus_potentiels} />
      </div>
    );
  }

  return (
    <form className="sauce-form" onSubmit={handleSubmit}>
      <div className="pots-table-wrap">
        <table className="esc-table pots-table">
          <thead>
            <tr>
              <th>Format</th>
              <th>SKU</th>
              <th>En ligne (WC)</th>
              <th>Produits ce batch</th>
              <th>Stock après</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>250 ml</strong></td>
              <td><code>SBBQMW-250</code></td>
              <td className="muted">{wcStock?.connected ? (wcStock.SBBQMW_250?.stock ?? '—') : '—'}</td>
              <td>
                <input
                  className="form-input pots-input"
                  type="number"
                  min="0"
                  value={pots250}
                  onChange={e => setPots250(e.target.value)}
                  onBlur={sauverBrouillon}
                  placeholder="0"
                  disabled={isFinalise}
                />
              </td>
              <td className="muted">
                {wcStock?.connected && pots250 !== ''
                  ? (wcStock.SBBQMW_250?.stock || 0) + (parseInt(pots250) || 0)
                  : '—'}
              </td>
            </tr>
            <tr>
              <td><strong>350 ml</strong></td>
              <td><code>SBBQMW-350</code></td>
              <td className="muted">{wcStock?.connected ? (wcStock.SBBQMW_350?.stock ?? '—') : '—'}</td>
              <td>
                <input
                  className="form-input pots-input"
                  type="number"
                  min="0"
                  value={pots350}
                  onChange={e => setPots350(e.target.value)}
                  onBlur={sauverBrouillon}
                  placeholder="0"
                  disabled={isFinalise}
                />
              </td>
              <td className="muted">
                {wcStock?.connected && pots350 !== ''
                  ? (wcStock.SBBQMW_350?.stock || 0) + (parseInt(pots350) || 0)
                  : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {mlProduits > 0 && (
        <div className="yield-panel">
          <div className="yield-row">
            <span>ml produits</span>
            <strong>{fmtMl(mlProduits)}</strong>
          </div>
          <div className="yield-row">
            <span>Rendement théorique</span>
            <strong>{fmtMl(rendTheo)}</strong>
          </div>
          <div className="yield-row">
            <span>Écart</span>
            <strong className={ecart >= 0 ? 'text-success' : 'text-danger'}>
              {ecart >= 0 ? '+' : ''}{ecart} ml
            </strong>
          </div>
          <div className="yield-row">
            <span>Yield</span>
            {badge ? <span className={`badge ${badge.cls}`}>{badge.label}</span> : '—'}
          </div>
          <div className="yield-row revenus-row">
            <span>Revenus potentiels</span>
            <strong className="gold">{revenus.toFixed(2)} $</strong>
          </div>
        </div>
      )}

      {/* Rentabilité — calcul live */}
      {(mlProduits > 0 || apercuDeduction?.cout_total_batch != null) && (() => {
        const nbUnites    = (parseInt(pots250) || 0) + (parseInt(pots350) || 0);
        const coutTotal   = apercuDeduction?.cout_total_batch != null ? apercuDeduction.cout_total_batch : null;
        const coutParUnite = (coutTotal != null && nbUnites > 0)
          ? Math.round(coutTotal / nbUnites * 100) / 100
          : null;
        const coutParKg   = (coutTotal != null && mlProduits > 0)
          ? Math.round(coutTotal / (mlProduits / 1000) * 100) / 100
          : null;
        const yieldPct    = rendTheo > 0 && mlProduits > 0
          ? Math.round((mlProduits / rendTheo) * 1000) / 10
          : null;
        const margeBrute  = (revenus > 0 && coutTotal != null)
          ? Math.round((revenus - coutTotal) / revenus * 1000) / 10
          : null;
        return (
          <div className="card" style={{
            marginTop: 16,
            padding: 14,
            background: 'rgba(200,148,26,0.06)',
            border: '1px solid rgba(200,148,26,0.25)',
          }}>
            <h4 style={{ marginBottom: 12 }}>Rentabilité — calcul live</h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 12,
            }}>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>Coût matières / pot</div>
                <div className="gold" style={{ fontSize: 20, fontWeight: 700 }}>
                  {coutParUnite != null ? `${coutParUnite.toFixed(2)} $` : '—'}
                </div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>Coût matières / L</div>
                <div className="gold" style={{ fontSize: 20, fontWeight: 700 }}>
                  {coutParKg != null ? `${coutParKg.toFixed(2)} $/L` : '—'}
                </div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>Yield (réel / théo)</div>
                <div className={
                  yieldPct == null ? 'muted'
                  : yieldPct >= 90 ? 'success'
                  : yieldPct >= 75 ? 'warning'
                  : 'danger'
                } style={{ fontSize: 20, fontWeight: 700 }}>
                  {yieldPct != null ? `${yieldPct.toFixed(1)} %` : '—'}
                </div>
                {rendTheo > 0 && (
                  <div className="muted" style={{ fontSize: 11 }}>
                    {fmtMl(mlProduits)} / {fmtMl(rendTheo)}
                  </div>
                )}
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>Revenu estimé</div>
                <div className="gold" style={{ fontSize: 20, fontWeight: 700 }}>
                  {revenus > 0 ? `${revenus.toFixed(2)} $` : '—'}
                </div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>Marge brute</div>
                <div className={
                  margeBrute == null ? 'muted'
                  : margeBrute >= 50 ? 'success'
                  : margeBrute >= 30 ? 'warning'
                  : 'danger'
                } style={{ fontSize: 20, fontWeight: 700 }}>
                  {margeBrute != null ? `${margeBrute.toFixed(1)} %` : '—'}
                </div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>Pots produits</div>
                <div className="gold" style={{ fontSize: 20, fontWeight: 700 }}>{nbUnites}</div>
              </div>
            </div>
          </div>
        );
      })()}

      {apercuDeduction?.deductions?.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ marginBottom: 10 }}>Aperçu déduction inventaire</h4>
          <table className="esc-table">
            <thead>
              <tr><th>Ingrédient</th><th>À déduire</th><th>Stock avant</th><th>Stock après</th></tr>
            </thead>
            <tbody>
              {apercuDeduction.deductions.map(d => (
                <tr key={d.matiere_id}>
                  <td>{d.nom}</td>
                  <td className="gold">−{d.quantite_deduite} {d.unite}</td>
                  <td className="muted">{d.stock_avant} {d.unite}</td>
                  <td className={d.statut === 'negatif' ? 'danger' : d.statut === 'bas' ? 'warning' : 'success'}>
                    {d.stock_apres} {d.unite}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {apercuDeduction.deductions.some(d => d.statut !== 'ok') && (
            <p className="warning" style={{ marginTop: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="attention" size={14} /> {apercuDeduction.deductions.filter(d => d.statut !== 'ok').length} ingrédient(s) à réapprovisionner
            </p>
          )}
        </div>
      )}

      {erreur && <div className="alert alert-danger">{erreur}</div>}

      <SectionProduitFini
        produit="sauce"
        form={finalForm}
        setForm={setFinalForm}
        batchId={batchData?.batchId}
        batchStatut={batchData?.form?.statut}
      />

      <div className="form-actions">
        <button type="button" className="btn btn-ghost" onClick={onBack}>← Retour</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Envoi...' : 'Confirmer et mettre à jour WooCommerce'}
        </button>
      </div>
    </form>
  );
}

// ─── ONGLET PLANIFICATEUR ─────────────────────

function Planificateur({ recette }) {
  const [mult, setMult]       = useState(1);
  const [calc, setCalc]       = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleCalc() {
    setLoading(true);
    try {
      const res = await api.get(`/sauce/calculateur?multiplicateur=${mult}`);
      setCalc(res);
    } catch (e) {
      console.error('[sauce] calculateur:', e);
    }
    setLoading(false);
  }

  const k = calc?.ketchup;
  const s = calc?.sauce;

  return (
    <div className="planificateur">
      <div className="plan-ctrl">
        <label>Nombre de batch(es) à produire</label>
        <div className="multiplicateur-ctrl">
          <button className="mult-btn" onClick={() => setMult(m => Math.max(1, m-1))}>−</button>
          <input
            className="form-input mult-input"
            type="number"
            min="1"
            max="20"
            value={mult}
            onChange={e => setMult(parseInt(e.target.value)||1)}
          />
          <button className="mult-btn" onClick={() => setMult(m => m+1)}>+</button>
        </div>
        <button className="btn btn-primary" onClick={handleCalc} disabled={loading}>
          {loading ? 'Calcul...' : 'Calculer'}
        </button>
      </div>

      {calc && (
        <>
          <div className="plan-sections">
            <div className="section-recette ketchup-section">
              <div className="section-header">
                <span className="section-icon"><Icon name="epices" size={20} /></span>
                <h3>KETCHUP MAISON</h3>
              </div>
              <table className="esc-table ingredients-table">
                <thead><tr><th>Ingrédient</th><th>Quantité ×{mult}</th></tr></thead>
                <tbody>
                  <tr><td>Pâte de tomate bio</td><td>{k.pate_tomate_cans} cans</td></tr>
                  <tr><td>Vinaigre de cidre pomme</td><td>{fmtMl(k.vinaigre_cidre_ml)}</td></tr>
                  <tr><td>Vinaigre blanc</td><td>{fmtMl(k.vinaigre_blanc_ml)}</td></tr>
                  <tr><td>Miel</td><td>{fmtMl(k.miel_ml)}</td></tr>
                  <tr><td>Eau</td><td>{fmtMl(k.eau_ml)}</td></tr>
                  <tr><td>Tallow fumé</td><td>{fmtMl(k.tallow_ml)}</td></tr>
                  <tr><td>Onion granulé</td><td>{fmtMl(k.onion_ml)}</td></tr>
                  <tr><td>Ail granulé</td><td>{fmtMl(k.ail_ml)}</td></tr>
                  <tr><td>Sel Kasher</td><td>{fmtMl(k.sel_kasher_ml)}</td></tr>
                  <tr><td>Sel de céleri</td><td>{fmtMl(k.sel_celeri_ml)}</td></tr>
                  <tr><td>Paprika fumé</td><td>{fmtMl(k.paprika_ml)}</td></tr>
                  <tr><td>Sauce soya</td><td>{fmtMl(k.sauce_soya_ml)}</td></tr>
                  <tr><td>Cassonade</td><td>{fmtMl(k.cassonade_ml)}</td></tr>
                </tbody>
              </table>
              <div className="rendement-row">Rendement estimé : ~{fmtMl(k.rendement_estime_ml)}</div>
            </div>

            <div className="section-recette sauce-section">
              <div className="section-header">
                <span className="section-icon"><Icon name="fumage" size={20} /></span>
                <h3>SAUCE BBQ MIEL &amp; BOURBON</h3>
              </div>
              <div className="sauce-sous-section">
                <div className="sous-section-titre">GASTRIQUE</div>
                <table className="esc-table ingredients-table">
                  <thead><tr><th>Ingrédient</th><th>Quantité</th></tr></thead>
                  <tbody>
                    <tr><td>Vinaigre de cidre pomme</td><td>{fmtMl(s.vinaigre_cidre_ml)}</td></tr>
                    <tr><td>Sucre de canne</td><td>{fmtMl(s.sucre_canne_ml)}</td></tr>
                    <tr><td>Cassonade</td><td>{fmtMl(s.cassonade_ml)}</td></tr>
                    <tr><td>Worcestershire</td><td>{fmtMl(s.worcestershire_ml)}</td></tr>
                    <tr><td>Sauce soya</td><td>{fmtMl(s.sauce_soya_ml)}</td></tr>
                  </tbody>
                </table>
              </div>
              <div className="sauce-sous-section">
                <div className="sous-section-titre">ÉPICES</div>
                <table className="esc-table ingredients-table">
                  <thead><tr><th>Ingrédient</th><th>Quantité</th></tr></thead>
                  <tbody>
                    <tr><td>Sel Kasher</td><td>{fmtMl(s.sel_kasher_ml)}</td></tr>
                    <tr><td>Poivre moulu</td><td>{fmtMl(s.poivre_ml)}</td></tr>
                    <tr><td>Poivre blanc</td><td>{fmtMl(s.poivre_blanc_ml)}</td></tr>
                    <tr><td>Poudre de Chili</td><td>{fmtMl(s.chili_ml)}</td></tr>
                    <tr><td>Ail granulé</td><td>{fmtMl(s.ail_ml)}</td></tr>
                    <tr><td>Onion granulé</td><td>{fmtMl(s.onion_ml)}</td></tr>
                    <tr><td>Épices de Jamaïque</td><td>{fmtMl(s.epices_jamaique_ml)}</td></tr>
                    <tr><td>Cumin</td><td>{fmtMl(s.cumin_ml)}</td></tr>
                  </tbody>
                </table>
              </div>
              <div className="sauce-sous-section">
                <div className="sous-section-titre">CORPS</div>
                <table className="esc-table ingredients-table">
                  <thead><tr><th>Ingrédient</th><th>Quantité</th></tr></thead>
                  <tbody>
                    <tr><td>Ketchup maison</td><td>{fmtMl(s.ketchup_ml)}</td></tr>
                    <tr><td>Miel</td><td>{fmtMl(s.miel_ml)}</td></tr>
                    <tr><td>Eau</td><td>{fmtMl(s.eau_ml)}</td></tr>
                    <tr><td>Bourbon américain</td><td>{fmtMl(s.bourbon_ml)}</td></tr>
                  </tbody>
                </table>
              </div>
              <div className="rendement-row">Rendement estimé : ~{fmtMl(s.rendement_estime_ml)}</div>
            </div>
          </div>

          {/* Suggestion de répartition */}
          <div className="card mix-card">
            <h4>Suggestion de répartition des pots</h4>
            <p className="muted">Avec ~{fmtMl(s.rendement_estime_ml)} :</p>
            <div className="mix-options">
              {calc.production_estimee.options_top3?.map((opt, i) => (
                <div key={i} className={`mix-option ${opt.recommande ? 'recommande' : ''}`}>
                  <div className="mix-label">
                    {opt.recommande && <span className="recommande-badge">RECOMMANDÉ</span>}
                    {opt.pots_350}×350ml + {opt.pots_250}×250ml = {fmtMl(opt.ml_utilises)}
                  </div>
                  <div className="mix-details muted">
                    Surplus: {opt.ml_restants}ml — Revenus: {opt.revenus.toFixed(2)}$
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── SECTION LISTE BATCHES ────────────────────

function SectionListeBatches({ onOuvrir, refreshTrigger }) {
  const [batches, setBatches]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [batchASupprimer, setBatchASupprimer] = useState(null);
  const [suppLoading, setSuppLoading]       = useState(false);
  const [toast, setToast]                   = useState('');
  const [erreur, setErreur]                 = useState('');

  const charger = useCallback(() => {
    setLoading(true);
    api.get('/sauce/batches')
      .then(setBatches)
      .catch(() => setBatches([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { charger(); }, [charger, refreshTrigger]);

  const afficherToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const supprimerBatch = async () => {
    if (!batchASupprimer) return;
    setSuppLoading(true);
    try {
      await api.delete(`/sauce/batches/${batchASupprimer.id}`);
      setBatches(prev => prev.filter(b => b.id !== batchASupprimer.id));
      afficherToast(`Batch ${batchASupprimer.nom} supprimé`);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setSuppLoading(false);
      setBatchASupprimer(null);
    }
  };

  return (
    <div className="card" style={{ marginTop: 24 }}>
      {batchASupprimer && (
        <ConfirmDeleteModal
          nomBatch={batchASupprimer.nom}
          loading={suppLoading}
          onConfirm={supprimerBatch}
          onAnnuler={() => setBatchASupprimer(null)}
        />
      )}
      {toast && <div className="toast-success">{toast}</div>}

      <h3>Tous les batches sauce BBQ</h3>
      {erreur && <p className="danger" style={{ marginBottom: 8 }}>{erreur}</p>}

      {loading ? (
        <p className="muted">Chargement...</p>
      ) : batches.length === 0 ? (
        <p className="muted" style={{ padding: '12px 0' }}>Aucun batch sauce enregistré.</p>
      ) : (
        <table className="esc-table">
          <thead>
            <tr>
              <th>Batch</th>
              <th>Date</th>
              <th>Multiplicateur</th>
              <th>Rendement théorique</th>
              <th>Pots produits</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {batches.map(b => {
              const potsLabel = (b.nb_pots_250_prod > 0 || b.nb_pots_350_prod > 0)
                ? `${b.nb_pots_250_prod} × 250ml + ${b.nb_pots_350_prod} × 350ml`
                : '—';
              return (
                <tr key={b.id}>
                  <td style={{ fontWeight: 600 }}>{b.nom}</td>
                  <td>{b.date_prod}</td>
                  <td>×{b.multiplicateur}</td>
                  <td>{fmtMl(b.rendement_theorique_ml)}</td>
                  <td>{potsLabel}</td>
                  <td>
                    <span className={`badge ${b.statut === 'finalise' ? 'badge-success' : 'badge-warning'}`}>
                      {b.statut === 'finalise' ? 'Finalisé' : 'En cours'}
                    </span>
                    {b.nb_tracabilite_manquante > 0 && (
                      <span className="badge badge--warning" style={{ marginLeft: 6 }}>
                        <Icon name="attention" size={11} /> {b.nb_tracabilite_manquante} sans facture
                      </span>
                    )}
                  </td>
                  <td className="td-actions">
                    <button className="btn btn-ghost btn-sm" onClick={() => onOuvrir(b)}>
                      Ouvrir →
                    </button>
                    {b.statut === 'finalise' && (
                      <Link className="btn btn-ghost btn-sm" to={`/batches/${b.id}/tracabilite`}>
                        Fiche de traçabilité
                      </Link>
                    )}
                    <button
                      className="btn btn-danger btn-sm btn-icon"
                      onClick={() => setBatchASupprimer(b)}
                      title="Supprimer ce batch"
                    >
                      <Icon name="supprimer" size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── SECTION FIFO ──────────────────────────────

function SectionFifo({ stock, onOuvrirAnalytique }) {
  if (!stock || stock.length === 0) {
    return (
      <div className="fifo-section">
        <h4>Ordre FIFO — Stock disponible</h4>
        <p className="muted">Aucun batch finalisé.</p>
      </div>
    );
  }

  return (
    <div className="fifo-section">
      <h4>Ordre FIFO — Stock disponible</h4>
      <div className="table-wrap">
        <table className="esc-table fifo-table">
          <thead>
            <tr>
              <th>Priorité</th>
              <th>Batch</th>
              <th>Date</th>
              <th>250ml dispo</th>
              <th>350ml dispo</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {stock.map(b => (
              <tr key={b.batch_id} className={b.fifo_priorite === 1 ? 'fifo-prioritaire' : ''}>
                <td>
                  {b.fifo_priorite === 1
                    ? <span className="badge badge-warning">1 ← EN PREMIER</span>
                    : <span className="muted">{b.fifo_priorite}</span>}
                </td>
                <td>{b.batch_nom}</td>
                <td className="muted">{b.date_prod}</td>
                <td>{b.pots_250.dispo > 0 ? <span className="badge badge-success">{b.pots_250.dispo}</span> : <span className="muted">0</span>}</td>
                <td>{b.pots_350.dispo > 0 ? <span className="badge badge-success">{b.pots_350.dispo}</span> : <span className="muted">0</span>}</td>
                <td>
                  <Link className="btn btn-ghost btn-sm" to={`/batches/${b.batch_id}/tracabilite`}>
                    Fiche
                  </Link>
                  {onOuvrirAnalytique && <button className="btn btn-ghost btn-sm" onClick={() => onOuvrirAnalytique(b.batch_id, b.batch_nom)} title="Analytique"><Icon name="analytics" size={14} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────

export default function ProductionSauce() {
  const [onglet, setOnglet]       = useState('production');
  const [etape, setEtape]         = useState(1);
  const [batchData, setBatchData] = useState(null);
  const [recette, setRecette]     = useState(null);
  const [stock, setStock]         = useState([]);
  const [successData, setSuccessData] = useState(null);
  const [batchAnalytique, setBatchAnalytique] = useState(null);
  const [nbTracabiliteNonLies, setNbTracabiliteNonLies] = useState(0);
  const [batchReprise, setBatchReprise]   = useState(null);
  const [listeRefreshTrigger, setListeRefreshTrigger] = useState(0);

  const ouvrirAnalytique = async (batchId, batchNom) => {
    try {
      const batches = await api.get('/sauce/batches');
      const b = batches.find(x => x.id === batchId);
      setBatchAnalytique(b || { id: batchId, nom: batchNom, produit: 'sauce' });
    } catch {
      setBatchAnalytique({ id: batchId, nom: batchNom, produit: 'sauce' });
    }
  };

  const chargerDonnees = useCallback(() => {
    api.get('/sauce/recette').then(setRecette).catch(e => console.error('[sauce] recette:', e));
    api.get('/sauce/stock').then(setStock).catch(e => console.error('[sauce] stock:', e));
  }, []);

  useEffect(() => { chargerDonnees(); }, [chargerDonnees]);

  // Reprendre un batch en_cours depuis la liste
  useEffect(() => {
    if (!batchReprise) return;
    const b = batchReprise;
    api.get(`/sauce/calculateur?multiplicateur=${b.multiplicateur}`)
      .then(calc => {
        setBatchData({
          batchId     : b.id,
          form        : b,
          recetteCalc : calc,
          rendementTheo: b.rendement_theorique_ml,
        });
        setSuccessData(null);
        setEtape(2);
        setBatchReprise(null);
      })
      .catch(() => {
        // Reprise sans recette calculée (dégradé)
        setBatchData({
          batchId     : b.id,
          form        : b,
          recetteCalc : null,
          rendementTheo: b.rendement_theorique_ml,
        });
        setSuccessData(null);
        setEtape(2);
        setBatchReprise(null);
      });
  }, [batchReprise]); // eslint-disable-line

  function handleBatchCreated(data) {
    setBatchData(data);
    setListeRefreshTrigger(t => t + 1);
    setEtape(2); // goes to Traçabilité
  }

  function handleProduction(data) {
    setSuccessData(data);
    setListeRefreshTrigger(t => t + 1);
    chargerDonnees();
  }

  function nouveauBatch() {
    setEtape(1);
    setBatchData(null);
    setSuccessData(null);
  }

  const isFinalise = batchData?.form?.statut === 'finalise';

  function handleStepClick(cible) {
    // Naviguer vers l'étape 1 affiche les données du batch courant (EtapeBatchEditer),
    // ou le formulaire de création si aucun batch n'est ouvert.
    // Ne PAS appeler nouveauBatch() ici — utiliser le bouton « + Nouveau batch » séparé.
    if (!batchData && cible > 1) return;
    setEtape(cible);
  }

  function handleOuvrir(b) {
    setBatchReprise(b);
    setOnglet('production');
  }

  return (
    <div className="sauce-page">
      {batchAnalytique && (
        <div className="modal-overlay" onClick={() => setBatchAnalytique(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, maxHeight: '90vh', overflowY: 'auto' }}>
            <BatchAnalytiquePanel batch={batchAnalytique} onFermer={() => setBatchAnalytique(null)} />
          </div>
        </div>
      )}
      <div className="page-header">
        <div>
          <h1 className="page-title">Production — Sauce BBQ Miel &amp; Bourbon</h1>
          <p className="page-sub">SKU 250ml : SBBQMW-250 — SKU 350ml : SBBQMW-350</p>
        </div>
        <Link to="/sauce/recette" className="btn btn-ghost">
          <Icon name="parametres" size={14} /> Modifier la recette
        </Link>
      </div>

      {/* Onglets */}
      <div className="onglets">
        <button
          className={`onglet-btn ${onglet === 'production' ? 'active' : ''}`}
          onClick={() => setOnglet('production')}
        >
          Production
        </button>
        <button
          className={`onglet-btn ${onglet === 'planificateur' ? 'active' : ''}`}
          onClick={() => setOnglet('planificateur')}
        >
          Planificateur
        </button>
      </div>

      {onglet === 'production' && (
        <div className="card stepper-card">
          {/* Bouton « + Nouveau batch » distinct du stepper */}
          {batchData && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={nouveauBatch}>
                + Nouveau batch
              </button>
            </div>
          )}
          <Stepper
            etape={etape}
            nbTracabiliteNonLies={nbTracabiliteNonLies}
            onStepClick={handleStepClick}
            hasBatch={!!batchData}
            isFinalise={isFinalise}
          />
          {etape === 1 && (batchData
            ? <EtapeBatchEditer batchData={batchData} onUpdated={(updated) => setBatchData(updated)} />
            : <EtapeBatch onNext={handleBatchCreated} recette={recette} />
          )}
          {etape === 2 && batchData && (
            <EtapeTracabilite
              batchId={batchData?.batchId}
              batchNom={batchData?.form?.nom}
              batchStatut={batchData?.form?.statut}
              onNext={() => setEtape(3)}
              onBack={() => setEtape(1)}
              onNbNonLiesChange={setNbTracabiliteNonLies}
            />
          )}
          {etape === 3 && batchData && (
            <EtapeRecette
              batchData={batchData}
              recetteCalc={batchData.recetteCalc}
              onNext={() => setEtape(4)}
              onBack={() => setEtape(2)}
            />
          )}
          {etape === 4 && batchData && (
            <EtapeProduction
              batchData={batchData}
              onSuccess={handleProduction}
              onBack={() => setEtape(3)}
            />
          )}
          {successData && (
            <div className="form-actions" style={{ marginTop: 8 }}>
              <button className="btn btn-ghost" onClick={nouveauBatch}>
                + Nouveau batch
              </button>
            </div>
          )}
        </div>
      )}

      {onglet === 'planificateur' && (
        <div className="card">
          <Planificateur recette={recette} />
        </div>
      )}

      {/* Liste de tous les batches — toujours visible */}
      <SectionListeBatches onOuvrir={handleOuvrir} refreshTrigger={listeRefreshTrigger} />

      {/* FIFO — toujours visible */}
      <SectionFifo stock={stock} onOuvrirAnalytique={ouvrirAnalytique} />
    </div>
  );
}
